import "whatwg-fetch";
import { optionsGen } from "../helpers/api";
import {
  toESQuery,
  toESGroupByQuery,
  toESDoubleGroupByQuery,
  toESDateHistogramQuery,
  toESDateHistogramGroupByQuery,
  toESHistogramQuery,
  cardinalityAggs,
  toESQueryWithAggs
} from "../helpers/query";
import { toTitleCase } from "../helpers/converters";
import { difference } from "lodash";

const AUTH = false;
export const SEARCH_IN_FIELDS = ["title", "abstract_fulltext", "description", "claims", "non_patent_citations"];
export const LARGE_FIELDS = ["abstract", "citations_npl", "citations_pat", "family_members", "abstract_fulltext", "description", "claims", "non_patent_citations"];

// scoping ES query to a specific landscape version
const SCOPED_FIELDS = ["alpha_score", "techs", "pools", "declared_as_sep", "pagerank"];
function scopeField(version, field) {
  return SCOPED_FIELDS.indexOf(field) >= 0 ? `${version}.${field}` : field;
}
function scopeObj(version, obj) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [scopeField(version, k), v]));
}
function scopeQuery(version, mq) {
  if (mq && mq.range) {
    return { range: scopeObj(version, mq.range) };
  }
  if (mq && mq.wildcard) {
    return { wildcard: scopeObj(version, mq.wildcard) };
  }
  if (mq && mq.bool && mq.bool.should) {
    return { bool: { should: mq.bool.should.map(mm => Object.fromEntries(Object.entries(mm).map(([k, mmobj]) => [k, scopeObj(version, mmobj)]))) } };
  }
  return mq;
}
export function injectScope(version, esq) {
  let mustNew = esq.query.bool.must.map(mq => scopeQuery(version, mq));
  mustNew.push({
    exists: {
      field: `${version}.alpha_score`
    }
  });
  esq.query.bool.must = mustNew;
  if (esq.query.bool.must_not) esq.query.bool.must_not = esq.query.bool.must_not.map(mq => scopeQuery(version, mq));
  if (esq.sort) esq.sort = esq.sort.map(s => scopeObj(version, s));
  return esq;
}

// map query params to actual fields
// 2nd arg version is no longer useful, but kept just in case it's needed again
export function remapQuery(queries) {
  // set default full_text_fields if not provided for a full_text query
  if (queries.full_text && !queries.full_text_fields) {
    return { ...queries, full_text_fields: SEARCH_IN_FIELDS };
  }
  return queries;
}

export function esqAssets(version, queries, idOnly = false, highlight = false) {
  const exclude = idOnly ? ["*"] : LARGE_FIELDS;
  return injectScope(version, toESQuery(remapQuery(queries, version), exclude, 20, highlight));
}

export function getAssets(esUrl, version, queries, idOnly = false) {
  const esq = esqAssets(version, queries, idOnly, true);
  return fetch(esUrl, optionsGen("POST", esq, AUTH)).then(resp => resp.json());
}

export function getPublicationCount(esUrl, version, queries) {
  const esq = esqAssets(version, queries, true);
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.hits.total.value);
}

export function getFamilyCount(esUrl, version, queries, familyField = "family_id") {
  const esq = cardinalityAggs(familyField, esqAssets(version, queries, true));
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.aggregations[familyField].value);
}

export function getPublicationByDate(esUrl, version, queries, dateField, interval = "quarter") {
  const esq = injectScope(version, toESDateHistogramQuery(dateField, interval, remapQuery(queries, version)));
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.aggregations[dateField].buckets);
}

export function getFamilyByDate(esUrl, version, queries, dateField, interval = "quarter", familyField = "family_id") {
  const esq = cardinalityAggs(familyField, injectScope(version, toESDateHistogramQuery(dateField, interval, remapQuery(queries, version))), dateField);
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json =>
      json.aggregations[dateField].buckets.map(e => ({
        key: e.key,
        key_as_string: e.key_as_string,
        doc_count: e[familyField].value
      }))
    );
}

export function getPublicationByDateAndField(esUrl, version, queries, dateField, field2Raw, interval = "quarter") {
  const field2 = scopeField(version, field2Raw);
  const esq = injectScope(version, toESDateHistogramGroupByQuery(dateField, field2, interval, remapQuery(queries, version)));
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json =>
      json.aggregations[dateField].buckets.map(b => ({
        key: b.key,
        key_as_string: b.key_as_string,
        value: b[field2].buckets.map(bb => ({
          key: bb.key,
          doc_count: bb.doc_count
        }))
      }))
    );
}

export function getFamilyByDateAndField(esUrl, version, queries, dateField, field2Raw, interval = "quarter", familyField = "family_id") {
  const field2 = scopeField(version, field2Raw);
  const esq = cardinalityAggs(familyField, injectScope(version, toESDateHistogramGroupByQuery(dateField, field2, interval, remapQuery(queries, version))), dateField, field2);
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json =>
      json.aggregations[dateField].buckets.map(b => ({
        key: b.key,
        key_as_string: b.key_as_string,
        value: b[field2].buckets.map(bb => ({
          key: bb.key,
          doc_count: bb[familyField].value
        }))
      }))
    );
}

export function getPublicationByScore(esUrl, version, queries, scoreFieldRaw = "alpha_score", interval = 1) {
  const scoreField = scopeField(version, scoreFieldRaw);
  const esq = injectScope(version, toESHistogramQuery(scoreField, interval, remapQuery(queries, version)));
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.aggregations[scoreField].buckets);
}

export function getFamilyByScore(esUrl, version, queries, scoreFieldRaw = "alpha_score", interval = 1, familyField = "family_id") {
  const scoreField = scopeField(version, scoreFieldRaw);
  const esq = cardinalityAggs(familyField, injectScope(version, toESHistogramQuery(scoreField, interval, remapQuery(queries, version))), scoreField);
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json =>
      json.aggregations[scoreField].buckets.map(e => ({
        key: e.key,
        doc_count: e[familyField].value
      }))
    );
}

export function getPublicationByField(esUrl, version, queries, fieldRaw, size = 20) {
  const field = scopeField(version, fieldRaw);
  const esq = injectScope(version, toESGroupByQuery(field, remapQuery(queries, version), size));
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      let total = json.aggregations[field].sum_other_doc_count;
      json.aggregations[field].buckets.forEach(b => {
        total += b.doc_count;
      });
      return json.aggregations[field].buckets.map(b => ({
        ...b,
        doc_pct: b.doc_count / total
      }));
    });
}

export function getFamilyByField(esUrl, version, queries, fieldRaw, size = 20, familyField = "family_id") {
  const field = scopeField(version, fieldRaw);
  const dynamicSize = size < 50 ? size * 2 : size;
  const esq = cardinalityAggs(familyField, injectScope(version, toESGroupByQuery(field, remapQuery(queries, version), dynamicSize)), field);
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      let total = json.aggregations[field].sum_other_doc_count;
      json.aggregations[field].buckets.forEach(b => {
        total += b[familyField].value;
      });
      const results = json.aggregations[field].buckets
        .map(b => ({
          key: b.key,
          doc_count: b[familyField].value,
          doc_pct: b[familyField].value / total
        }))
        .sort((a, b) => {
          return b.doc_count - a.doc_count;
        });
      return results.slice(0, size);
    });
}

export function getPublicationByTwoFields(esUrl, version, queries, field1Raw, field2Raw, size = 20) {
  const field1 = scopeField(version, field1Raw);
  const field2 = scopeField(version, field2Raw);
  const esq = injectScope(version, toESDoubleGroupByQuery(field1, field2, remapQuery(queries, version), size));
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      let total = json.aggregations[field1].sum_other_doc_count;
      json.aggregations[field1].buckets.forEach(b => {
        total += b.doc_count;
      });
      return json.aggregations[field1].buckets.map(b => ({
        key: b.key,
        value: b[field2].buckets.map(bb => ({
          key: bb.key,
          doc_count: bb.doc_count,
          doc_pct: bb.doc_count / total
        }))
      }));
    });
}

export function getFamilyByTwoFields(esUrl, version, queries, field1Raw, field2Raw, size = 20, familyField = "family_id") {
  const field1 = scopeField(version, field1Raw);
  const field2 = scopeField(version, field2Raw);
  const esq = cardinalityAggs(familyField, injectScope(version, toESDoubleGroupByQuery(field1, field2, remapQuery(queries, version), size)), field1, field2);
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      let total = json.aggregations[field1].sum_other_doc_count;
      json.aggregations[field1].buckets.forEach(b => {
        b[field2].buckets.forEach(bb => {
          total += bb[familyField].value;
        });
      });
      return json.aggregations[field1].buckets.map(b => ({
        key: b.key,
        value: b[field2].buckets.map(bb => ({
          key: bb.key,
          doc_count: bb[familyField].value,
          doc_pct: bb[familyField].value / total
        }))
      }));
    });
}

export function getDistinctValues(esUrl, version, fieldRaw, queries) {
  const field = scopeField(version, fieldRaw);
  const esq = injectScope(version, toESGroupByQuery(field, remapQuery(queries, version), 100));
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.aggregations[field].buckets.map(e => toTitleCase(e.key)));
}

export function checkPublicationInclusion(esUrl, version, publications) {
  const esq = {
    query: {
      bool: {
        must: [
          { ids: { values: publications } },
          {
            exists: {
              field: `${version}.alpha_score`
            }
          }
        ]
      }
    },
    size: 10000,
    _source: false
  };
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      const included = json.hits.hits.map(e => e._id);
      return { in: included, not_in: difference(publications, included) };
    });
}

export function checkFamilyIdInclusion(esUrl, version, familyIds) {
  const esq = injectScope(version, toESGroupByQuery("family_id", { family_id: familyIds }, familyIds.length));
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      const included = json.aggregations["family_id"].buckets.map(e => e.key);
      return { in: included, not_in: difference(familyIds, included) };
    });
}

export function getDistinctFamilyID(esUrl, version, pubs, extended = false) {
  const fieldFamilyId = extended ? "extended_family_id" : "family_id";
  let esq = {
    from: 0,
    size: pubs.length,
    query: {
      ids: {
        values: pubs
      }
    },
    _source: {
      include: [fieldFamilyId]
    }
  };
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      const familyIdSet = new Set();
      json.hits.hits.forEach(hit => {
        familyIdSet.add(hit._source[fieldFamilyId]);
      });
      return [...familyIdSet];
    });
}

export function expandPublicationNumbersByFamilies(esUrl, version, familyIDs, extended = false) {
  const fieldFamilyId = extended ? "extended_family_id" : "family_id";
  // version scope is not needed for family expansion, but keep version in function args for consistency
  let esq = {
    from: 0,
    size: 10000,
    query: {
      bool: {
        filter: [{ terms: { [fieldFamilyId]: [...familyIDs] } }]
      }
    },
    _source: {
      exclude: ["*"]
    }
  };
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.hits.hits.map(hit => hit._id));
}

export function expandPublicationNumbersByCiting(esUrl, version, pubs) {
  let esq = {
    from: 0,
    size: 10000,
    query: {
      ids: {
        values: pubs
      }
    },
    _source: {
      include: ["citations_pat"]
    }
  };
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      const citationSet = new Set();
      json.hits.hits.forEach(hit => {
        hit._source.citations_pat.forEach(c => {
          citationSet.add(c);
        });
      });
      return [...citationSet];
    });
}

export function expandPublicationNumbersByCitedBy(esUrl, version, pubs) {
  let esq = {
    from: 0,
    size: 10000,
    query: {
      terms: {
        citations_pat: pubs
      }
    },
    _source: {
      exclude: ["*"]
    }
  };
  return fetch(esUrl, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      const citedBySet = new Set();
      json.hits.hits.forEach(hit => {
        citedBySet.add(hit._id);
      });
      return [...citedBySet];
    });
}

export function getDocumentsWithAggregations(esUrl, query, aggs, version) {
  let q = toESQueryWithAggs(query, aggs);
  q = injectScope(version, q);
  return fetch(esUrl, optionsGen("POST", q, AUTH))
    .then(resp => resp.json())
    .then(json => {
      return json;
    });
}
